/**
 * Dados de exemplo da Membresia: o público dos cultos passados, endereços em Natal com a
 * coordenada da rua e algumas entradas no último ano. O seed (`createSampleState`) usa as
 * mesmas funções, e o script do servidor `pnpm db:sample-membership` as aplica a um banco
 * de demonstração que já existe — **completando**, sem apagar nem sobrescrever o que foi
 * cadastrado: só o culto sem público, o membro sem endereço no RN e, se quase ninguém
 * entrou no último ano, a data de entrada de alguns.
 */
import { joinsIn } from './community';
import { addDays, diffDays, hashString, normalize } from './dates';
import { LEGAL_VERSION } from './legal';
import { NATAL_SAMPLE_HOMES, type Zone, districtOf, sampleAddress, sampleLocation } from './geography';
import { newMember } from './transitions';
import { referToRegistration } from './visitors';
import type {
  AgeRange,
  AvailState,
  CivilStatus,
  DomainState,
  JoinedVia,
  Member,
  Ministry,
  Registration,
  ScheduleEvent,
  Sex,
  Visitor,
  VisitorContact,
  VisitorFollowUp,
  VisitorSource,
  VisitorVisit,
} from './types';

type AudienceBase = { total: number; kids: number; online: boolean };

/** Público típico de cada série de exemplo: o presencial e a fração de crianças. */
const SERIES_BASE: Record<string, AudienceBase> = {
  'dom-m': { total: 236, kids: 0.19, online: true },
  'dom-n': { total: 318, kids: 0.1, online: true },
  qua: { total: 92, kids: 0, online: false },
};

/** O público típico pelo título, para os eventos que não são das séries de exemplo. */
function audienceBaseOf(event: ScheduleEvent): AudienceBase {
  const seeded = SERIES_BASE[event.seriesId];

  if (seeded) return seeded;

  const title = normalize(event.title);

  if (title.includes('manha')) return SERIES_BASE['dom-m']!;
  if (title.includes('noite')) return SERIES_BASE['dom-n']!;
  if (title.includes('oracao')) return SERIES_BASE.qua!;
  if (title.includes('jovens')) return { total: 188, kids: 0, online: true };

  return { total: 180, kids: 0.12, online: true };
}

/**
 * Registra o público dos cultos que já aconteceram e ainda não têm registro, com números
 * estáveis a partir do id e uma leve alta ao longo das semanas. Os `pending` cultos mais
 * recentes sem registro ficam assim — a pendência "público não registrado" — e o de hoje
 * também, porque ainda não acabou. O que já tem registro não muda.
 */
export function sampleAudiences(
  events: ScheduleEvent[],
  today: string,
  recorders: readonly string[],
  pending = 2
): ScheduleEvent[] {
  const missing = events
    .filter((e) => e.date < today && !e.audience)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const left = new Set(pending > 0 ? missing.slice(-pending).map((e) => e.id) : []);
  const order = new Map(missing.map((e, i) => [e.id, i]));
  const names = recorders.length ? recorders : ['Secretaria'];

  return events.map((e) => {
    const index = order.get(e.id);

    if (index === undefined || left.has(e.id)) return e;

    const base = audienceBaseOf(e);
    const h = hashString(`${e.id}publico`);
    const rand = (salt: number) => ((h >>> salt) % 1000) / 1000;
    const growth = 1 + 0.004 * (index % 24);
    const total = Math.round(base.total * growth * (0.9 + rand(0) * 0.2));
    const at = Date.parse(`${e.date}T${e.time < '12:00' ? '12:30' : '21:30'}:00-03:00`);

    return {
      ...e,
      audience: {
        total,
        members: Math.round(total * (0.66 + rand(2) * 0.08)),
        firstTime: Math.round(total * (0.03 + rand(4) * 0.03)),
        recurring: Math.round(total * (0.05 + rand(6) * 0.03)),
        kids: base.kids ? Math.round(total * base.kids * (0.85 + rand(8) * 0.3)) : null,
        online: base.online ? Math.round(40 + rand(10) * 90) : null,
        method: rand(12) < 0.7 ? 'manual' : 'estimate',
        note: '',
        recordedBy: names[h % names.length]!,
        recordedAt: at,
        updatedBy: null,
        updatedAt: null,
      },
    };
  });
}

/**
 * Dá uma casa de exemplo em Natal (com a coordenada da rua) a quem não tem endereço no
 * Rio Grande do Norte. O casal fica na mesma casa; as casas são distribuídas em ordem, e o
 * número sai do id — estável a cada execução.
 */
export function sampleHomes(members: Member[], now: number): Member[] {
  const needs = (m: Member) => m.inactiveSince === null && m.address.uf.toUpperCase() !== 'RN';
  const byId = new Map(members.map((m) => [m.id, m]));
  const assigned = new Map<string, Member>();
  let next = 0;

  for (const m of members) {
    if (!needs(m) || assigned.has(m.id)) continue;

    const home = NATAL_SAMPLE_HOMES[next % NATAL_SAMPLE_HOMES.length]!;
    const h = hashString(`${m.id}casa`);
    const number = String(10 + (h % 1900));
    const address = sampleAddress(home, number, h % 3 === 0 ? `Apto ${10 + (h % 90)}` : '');
    const location = sampleLocation(home, number, now);

    next += 1;
    assigned.set(m.id, { ...m, address, location });

    const spouse = m.spouseId ? byId.get(m.spouseId) : undefined;

    if (spouse && needs(spouse)) assigned.set(spouse.id, { ...spouse, address, location });
  }

  return members.map((m) => assigned.get(m.id) ?? m);
}

/** Quantos dias antes de hoje entram os membros de exemplo do último ano. */
const RECENT_JOINS = [40, 115, 190, 250, 300, 340];

/**
 * Se quase ninguém entrou no último ano (menos de 3), alguns membros ativos ganham a data
 * de entrada ("Membro desde") nos últimos 12 meses — a evolução da Membresia deixa de ser
 * uma linha reta. A escolha é estável (pelo hash do id).
 */
export function sampleJoins(members: Member[], today: string): Member[] {
  const year = { from: addDays(today, -364), to: today, days: 365 };

  if (joinsIn(members, year) >= 3) return members;

  const picked = members
    .filter((m) => m.inactiveSince === null && !m.admin)
    .sort((a, b) => hashString(`${a.id}entrada`) - hashString(`${b.id}entrada`))
    .slice(0, RECENT_JOINS.length);
  const since = new Map(picked.map((m, i) => [m.id, addDays(today, -RECENT_JOINS[i]!)]));

  return members.map((m) => (since.has(m.id) ? { ...m, memberSince: since.get(m.id)! } : m));
}

/**
 * Como os membros de exemplo se dividem pelas zonas de Natal: aproximadamente a população
 * de cada zona (Norte a mais populosa; Leste, a menor, com os bairros históricos).
 */
export const ZONE_SHARE: Record<Zone, number> = { N: 0.35, L: 0.17, O: 0.22, S: 0.26 };

const MALE_NAMES = [
  'Rodrigo',
  'Felipe',
  'Mateus',
  'Gustavo',
  'Leonardo',
  'Henrique',
  'Davi',
  'Samuel',
  'Caio',
  'Victor',
  'Raul',
  'Igor',
  'Elias',
  'Jonas',
  'Tadeu',
];
const FEMALE_NAMES = [
  'Juliana',
  'Fernanda',
  'Aline',
  'Priscila',
  'Beatriz',
  'Sabrina',
  'Larissa',
  'Débora',
  'Raquel',
  'Natália',
  'Vanessa',
  'Tatiane',
  'Rebeca',
  'Mônica',
  'Eliane',
];
const SURNAMES = [
  'Albuquerque',
  'Bezerra',
  'Câmara',
  'Dantas',
  'Fernandes',
  'Galvão',
  'Lopes',
  'Medeiros',
  'Nóbrega',
  'Pereira',
  'Queiroz',
  'Rocha',
  'Santiago',
  'Trindade',
  'Vasconcelos',
  'Wanderley',
  'Azevedo',
  'Cavalcanti',
  'Fonseca',
  'Gurgel',
];
const JOBS = [
  'Professor(a)',
  'Enfermeiro(a)',
  'Técnico(a) de TI',
  'Comerciante',
  'Motorista',
  'Estudante',
  'Contador(a)',
  'Designer',
  'Autônomo(a)',
  'Pedagogo(a)',
  'Vendedor(a)',
  'Fisioterapeuta',
];
const VIA: JoinedVia[] = ['baptism', 'baptism', 'profession', 'transfer', 'baptism', 'acclamation'];
const CIVIL: CivilStatus[] = ['single', 'married', 'married', 'single', 'stableUnion', 'divorced'];
/** Posições (`dia * 3 + período`) livres em cada padrão: domingo, quarta à noite e outros. */
const OPEN_SLOTS = [
  [0, 1, 2, 11],
  [1, 2, 11, 20],
  [0, 2, 11, 14],
];

/** Quantos membros ativos cada zona deve ter para somar `total`, pela divisão de `ZONE_SHARE`. */
export function zoneTargets(total: number): Record<Zone, number> {
  const zones = Object.keys(ZONE_SHARE) as Zone[];
  const base = Object.fromEntries(zones.map((z) => [z, Math.floor(total * ZONE_SHARE[z])])) as Record<
    Zone,
    number
  >;
  let left = total - zones.reduce((sum, z) => sum + base[z], 0);

  // As sobras vão para quem tem a maior parte fracionária.
  for (const z of [...zones].sort((a, b) => ((total * ZONE_SHARE[b]) % 1) - ((total * ZONE_SHARE[a]) % 1))) {
    if (left-- > 0) base[z] += 1;
  }

  return base;
}

function pick<T>(list: readonly T[], h: number): T {
  return list[h % list.length]!;
}

/**
 * Completa o cadastro de exemplo até `target` membros ativos, **sem mexer em quem já
 * existe**: cada novo membro mora numa casa de exemplo (rua real de Natal, com a
 * coordenada do OpenStreetMap) da zona que mais está abaixo da sua parte (`ZONE_SHARE`), de
 * modo que o total fique proporcional às zonas. Os dados são estáveis a partir do id: nome
 * (sem repetir o de ninguém), contato, estado civil, profissão, situação e tempo de igreja
 * (alguns entram no último ano), contato de emergência, disponibilidade e, nos ministérios
 * dados, a habilitação em uma ou duas funções (respeitando o sexo da função).
 */
export function growMembers(
  members: Member[],
  target: number,
  today: string,
  now: number,
  ministries: readonly Ministry[] = []
): Member[] {
  const active = members.filter((m) => m.inactiveSince === null);
  const missing = target - active.length;

  if (missing <= 0) return members;

  const zones = Object.keys(ZONE_SHARE) as Zone[];
  const have = Object.fromEntries(zones.map((z) => [z, 0])) as Record<Zone, number>;

  for (const m of active) {
    const zone = districtOf(m.address)?.zone;

    if (zone) have[zone] += 1;
  }

  const homesOf = (zone: Zone) =>
    NATAL_SAMPLE_HOMES.filter(
      (h) => districtOf({ district: h.district, city: h.city, uf: h.uf })?.zone === zone
    );
  const wanted = zoneTargets(target);
  const used = new Set(members.map((m) => normalize(m.name)));
  const emails = new Set(members.map((m) => m.email.toLowerCase()));
  const ids = new Set(members.map((m) => m.id));
  const year = Number(today.slice(0, 4));
  const added: Member[] = [];
  const roles = ministries.flatMap((m) => m.roles.map((r) => ({ ministry: m.id, role: r })));

  for (let i = 0; i < missing; i += 1) {
    // A zona que mais está abaixo da meta (em fração dela) recebe o próximo.
    const zone = [...zones].sort(
      (a, b) => have[a] / wanted[a] - have[b] / wanted[b] || ZONE_SHARE[b] - ZONE_SHARE[a]
    )[0]!;
    const homes = homesOf(zone);

    let n = members.length + i + 1;

    while (ids.has(`g${n}`)) n += 1;

    const id = `g${n}`;

    ids.add(id);

    const h = hashString(`${id}novo`);
    const sex: Sex = i % 2 === 0 ? 'F' : 'M';
    let name = '';

    for (let attempt = 0; !name || used.has(normalize(name)); attempt += 1) {
      const first = pick(sex === 'F' ? FEMALE_NAMES : MALE_NAMES, h + attempt * 7);
      const last = [pick(SURNAMES, (h >>> 3) + attempt), pick(SURNAMES, (h >>> 7) + attempt * 3 + 5)];

      name = `${first} ${last[0] === last[1] ? last[0] : last.join(' ')}`;
    }

    used.add(normalize(name));

    const slug = normalize(name).replace(/\s+/g, '.');
    const email = emails.has(`${slug}@exemplo.com`) ? '' : `${slug}@exemplo.com`;

    emails.add(email);

    const age = 19 + (h % 48);
    const birth = `${year - age}-${String((h % 12) + 1).padStart(2, '0')}-${String(((h >>> 4) % 28) + 1).padStart(2, '0')}`;
    const home = homes[(have[zone] + i) % homes.length]!;
    const number = String(12 + (hashString(`${id}casa`) % 1800));
    const recent = i % 5 === 1;
    const since = recent
      ? addDays(today, -(30 + ((h >>> 8) % 300)))
      : `${year - 1 - (h % 12)}-${String(((h >>> 6) % 12) + 1).padStart(2, '0')}-10`;
    const via = pick(VIA, h >>> 5);
    const base = newMember(id, { name, birthDate: birth > today ? null : birth, sex });
    const eligible = roles.filter(({ role }) => role.allowedSex === 'any' || role.allowedSex === sex);
    const enabled: Record<string, string[]> = {};

    if (eligible.length) {
      for (const k of [0, 1]) {
        if (k === 1 && h % 3 !== 0) break;

        const { ministry, role } = eligible[(h >>> (9 + k)) % eligible.length]!;

        enabled[ministry] = [...new Set([...(enabled[ministry] ?? []), role.id])];
      }
    }

    added.push({
      ...base,
      email,
      phone: `849${String(hashString(`${id}tel`) % 100_000_000).padStart(8, '0')}`,
      roles: enabled,
      availability: Array.from({ length: 21 }, (_, slot): AvailState =>
        pick(OPEN_SLOTS, h >>> 11).includes(slot) ? 'available' : 'unset'
      ),
      civilStatus: pick(CIVIL, h >>> 2),
      profession: pick(JOBS, h >>> 13),
      churchStatus: 'active',
      memberSince: since,
      joinedVia: via,
      baptismDate: via === 'baptism' ? since : `${year - 4 - (h % 9)}-06-15`,
      originChurch: via === 'transfer' || via === 'acclamation' ? 'Igreja Batista do Alecrim' : '',
      address: sampleAddress(home, number),
      location: sampleLocation(home, number, now),
      emergency: {
        name: `${pick(['Maria', 'José', 'Ana', 'Paulo'], h >>> 15)} ${name.split(' ').at(-1)}`,
        relation: pick(['Mãe', 'Pai', 'Irmão(ã)', 'Cônjuge'], h >>> 17),
        phone: `849${String(hashString(`${id}sos`) % 100_000_000).padStart(8, '0')}`,
      },
    });
    have[zone] += 1;
  }

  return [...members, ...added];
}

export interface SampleSummary {
  audiences: number;
  addresses: number;
  joins: number;
  /** Membros novos, para chegar ao total pedido (`members`). */
  added: number;
  /** Fichas de visitantes criadas (só quando não havia nenhuma). */
  visitors: number;
}

/**
 * Completa a Membresia de um estado que já existe (o banco de demonstração). Com
 * `options.members`, também cadastra membros novos até esse total de ativos
 * (`growMembers`, habilitados nos ministérios do estado).
 */
export function completeMembershipSample<
  S extends Pick<DomainState, 'members' | 'events'> &
    Partial<Pick<DomainState, 'ministries' | 'visitors' | 'registrations'>>,
>(
  state: S,
  today: string,
  now: number,
  options: { members?: number } = {}
): { state: S; summary: SampleSummary } {
  const recorders = state.members.filter((m) => m.admin && m.inactiveSince === null).map((m) => m.name);
  const events = sampleAudiences(state.events, today, recorders);
  const grown = options.members
    ? growMembers(state.members, options.members, today, now, state.ministries)
    : state.members;
  const homed = sampleHomes(grown, now);
  const members = sampleJoins(homed, today);
  const changed = <T>(a: readonly T[], b: readonly T[]) => a.filter((x, i) => x !== b[i]).length;
  const extra: Partial<Pick<DomainState, 'visitors' | 'registrations'>> = {};

  // As fichas de exemplo só entram onde ainda não há nenhuma (o estado traz `visitors`).
  if (state.visitors && !state.visitors.length) {
    const welcome = (state.ministries ?? []).filter((m) => m.welcome);
    const owners = welcome.flatMap((m) => [...m.leaderIds, ...m.roles.flatMap((r) => r.leaderIds)]);
    const admins = members.filter((m) => m.admin && m.inactiveSince === null);
    const ownerIds = [...new Set(owners.length ? owners : admins.map((m) => m.id))];
    const staff = members.find((m) => m.id === ownerIds[0]) ?? admins[0];
    const sample = sampleVisitors({
      events,
      today,
      ownerIds,
      inviterIds: members
        .filter((m) => m.inactiveSince === null)
        .slice(0, 12)
        .map((m) => m.id),
      staffName: staff?.name ?? 'Secretaria',
    });

    extra.visitors = sample.visitors;

    if (sample.referral && state.registrations)
      extra.registrations = [...state.registrations, sample.referral];
  }

  return {
    state: { ...state, events, members, ...extra },
    summary: {
      audiences: changed(events, state.events),
      addresses: changed(homed, grown),
      joins: changed(members, homed),
      added: grown.length - state.members.length,
      visitors: extra.visitors?.length ?? 0,
    },
  };
}

// --- Visitantes ----------------------------------------------------------------------------

const VISITOR_FIRST = [
  'Thiago',
  'Lívia',
  'Marcelo',
  'Clara',
  'André',
  'Yasmin',
  'Rogério',
  'Manuela',
  'Sérgio',
  'Isadora',
  'Wesley',
  'Bianca',
  'Murilo',
  'Letícia',
  'Fábio',
  'Patrícia',
  'Diego',
  'Carolina',
  'Emerson',
  'Joana',
  'Lucas',
  'Valéria',
  'Renan',
  'Simone',
  'Alex',
  'Gabriela',
  'Ítalo',
  'Helena',
];
const VISITOR_LAST = [
  'Macedo',
  'Freire',
  'Paiva',
  'Saraiva',
  'Teixeira',
  'Morais',
  'Holanda',
  'Brito',
  'Seabra',
];
const VISITOR_SOURCE_WEIGHT: [VisitorSource, number][] = [
  ['invite', 0.45],
  ['social', 0.2],
  ['passing', 0.12],
  ['event', 0.1],
  ['online', 0.1],
  ['other', 0.03],
];
const VISITOR_AGES: AgeRange[] = ['18-29', '18-29', '30-44', '30-44', '45-59', '60+', 'unknown'];

/** Quantas fichas de exemplo, no máximo (bem menos que os "primeira vez" contados). */
export const SAMPLE_VISITORS = 28;

/**
 * Fichas de visitantes de exemplo, presas aos cultos registrados dos últimos 6 meses e em
 * número bem menor que os "primeira vez" contados (nem todo visitante deixa a ficha):
 * algumas voltaram, cada uma numa etapa; duas com acompanhamento atrasado, a mais recente
 * vinda do link e sem responsável, uma encaminhada ao cadastro (`referral`, a solicitação
 * pendente) e uma arquivada por "não quer mais contato". Estáveis a cada execução.
 */
export function sampleVisitors(input: {
  events: readonly ScheduleEvent[];
  today: string;
  ownerIds: readonly string[];
  inviterIds: readonly string[];
  /** Quem registrou as fichas da equipe e fez os contatos. */
  staffName: string;
  /** Prefixo dos ids (fichas e cadastro): os ids do banco valem para todas as igrejas. */
  idPrefix?: string;
}): { visitors: Visitor[]; referral: Registration | null } {
  const { events, today, ownerIds, inviterIds, staffName, idPrefix = '' } = input;
  const services = events
    .filter((e) => e.date < today && e.date >= addDays(today, -180) && (e.audience?.firstTime ?? 0) > 0)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  if (!services.length) return { visitors: [], referral: null };

  const count = Math.min(SAMPLE_VISITORS, services.length);
  const stamp = (date: string, hour = 12) =>
    Date.parse(`${date}T${String(hour).padStart(2, '0')}:00:00-03:00`);
  const districts = NATAL_SAMPLE_HOMES.map((h) => h.district);
  const visitors: Visitor[] = [];
  let referral: Registration | null = null;
  let overdue = 0;

  for (let i = 0; i < count; i++) {
    const first = services[Math.floor((i * services.length) / count)]!;
    const name = `${VISITOR_FIRST[i % VISITOR_FIRST.length]} ${pick(VISITOR_LAST, i * 7 + 3)}`;
    const h = hashString(`${name}visitante`);
    const rand = (salt: number) => ((h >>> salt) % 1000) / 1000;
    const id = `${idPrefix}v${i + 1}`;
    const age = diffDays(first.date, today);
    const later = services.filter((e) => e.date > first.date);
    const returns = rand(1) < 0.4 && later.length ? 1 + Math.floor(rand(3) * Math.min(3, later.length)) : 0;
    const visits: VisitorVisit[] = [
      { date: first.date, eventId: first.id },
      ...Array.from(
        { length: returns },
        (_, k) => later[Math.floor(((k + 1) * later.length) / (returns + 1))]!
      )
        .filter((e, k, list) => list.findIndex((x) => x.id === e.id) === k)
        .map((e) => ({ date: e.date, eventId: e.id })),
    ];
    const isLast = i === count - 1;
    const origin: Visitor['origin'] = isLast || rand(5) < 0.25 ? 'link' : 'staff';
    const wantsContact = isLast || rand(7) < 0.85;
    let stage = 'contacted';

    if (isLast) stage = 'new';
    else if (!wantsContact) stage = 'archived';
    else if (age <= 10) stage = rand(9) < 0.5 ? 'new' : 'contacted';
    else if (visits.length >= 3) stage = 'integrating';
    else if (visits.length === 2) stage = 'returned';
    else if (age > 120) stage = rand(9) < 0.5 ? 'archived' : 'contacted';

    let weight = rand(11);
    const source = VISITOR_SOURCE_WEIGHT.find(([, w]) => (weight -= w) < 0)?.[0] ?? 'other';
    const ownerId = isLast || !ownerIds.length ? null : ownerIds[i % ownerIds.length]!;
    const contacts: VisitorContact[] =
      stage === 'new'
        ? []
        : [
            {
              id: `${id}c1`,
              at: stamp(addDays(first.date, 2), 19),
              kind: 'whatsapp',
              by: staffName,
              outcome: stage === 'archived' && !wantsContact ? 'declined' : 'reached',
            },
          ];
    const open = stage !== 'archived' && !isLast;
    // Os dois primeiros abertos com prazo vencido; os demais, nos próximos dias.
    const late = open && overdue < 2 && stage !== 'new';

    if (late) overdue += 1;

    const followUps: VisitorFollowUp[] = open
      ? [
          {
            id: `${id}f1`,
            kind: stage === 'integrating' ? 'visit' : 'call',
            ownerId,
            due: late ? addDays(today, -3 - overdue * 2) : addDays(today, 2 + (i % 9)),
            status: stage === 'integrating' ? 'doing' : 'pending',
            note: stage === 'integrating' ? 'Convidar para o encontro de novos membros.' : '',
            doneAt: null,
            doneBy: '',
          },
        ]
      : [];
    const phone = `849${String(10_000_000 + (h % 89_999_999)).slice(0, 8)}`;
    const visitor: Visitor = {
      id,
      name,
      phone,
      email: rand(13) < 0.35 ? `${normalize(name).replace(/\s+/g, '.')}@exemplo.com` : '',
      ageRange: VISITOR_AGES[h % VISITOR_AGES.length]!,
      district: districts[h % districts.length] ?? '',
      source,
      invitedById: source === 'invite' && inviterIds.length ? inviterIds[h % inviterIds.length]! : null,
      visits,
      stage,
      ownerId,
      wantsContact,
      wantsMembership: stage === 'integrating' || rand(15) < 0.15,
      consent: { at: stamp(first.date), version: LEGAL_VERSION, by: origin === 'link' ? 'self' : 'staff' },
      origin,
      registrationId: null,
      note: '',
      followUps,
      contacts,
      archivedAt: stage === 'archived' ? stamp(addDays(first.date, 3)) : null,
      anonymized: false,
      createdAt: stamp(first.date),
      createdBy: origin === 'link' ? '' : staffName,
    };

    visitors.push(visitor);
  }

  // A primeira ficha "em integração" que quer conhecer a membresia vai para o cadastro.
  const referred = visitors.find((v) => v.stage === 'integrating' && v.wantsMembership);

  if (referred) {
    const at = stamp(addDays(today, -1), 10);
    const result = referToRegistration({ visitors, registrations: [] }, referred.id, `${idPrefix}reg-v1`, at);

    referral = result.registrations[0] ?? null;
    visitors.splice(0, visitors.length, ...result.visitors);
  }

  return { visitors, referral };
}
